import type { CockpitMetricView } from './cockpitViewModel';

type MetricReason = CockpitMetricView['reasonCode'];

const GUIDANCE: Record<
  NonNullable<MetricReason>,
  Pick<CockpitMetricView, 'impact' | 'action'>
> = {
  'no-usage-observed': {
    impact: 'Não há base observável para apresentar uma contagem de tokens.',
    action:
      'Confirme se a execução emitiu métricas; não substitua ausência por zero.',
  },
  'usage-id-missing': {
    impact:
      'O uso observado não pode ser reconciliado nem somado com segurança.',
    action:
      'Atualize o runtime ou bundle nas próximas execuções; não estime o total manualmente.',
  },
  'token-side-not-reported': {
    impact:
      'Somente o lado efetivamente reportado pode ser considerado confiável.',
    action:
      'Verifique a capacidade do provedor para este lado sem completar o valor com zero.',
  },
  'legacy-measurement-only': {
    impact:
      'Só existem registros de medição antiga; nenhum total faturado pode ser exibido (ADR-006).',
    action:
      'Consulte a medição antiga como referência separada; não a some ao total faturado.',
  },
  'cost-source-not-received': {
    impact: 'Nenhuma fonte reportou o custo desta atividade até o corte.',
    action: 'Aguarde a origem de custo; não estime o valor manualmente.',
  },
  'time-window-unavailable': {
    impact:
      'Não existe uma janela temporal canônica para interpretar esta duração.',
    action:
      'Aguarde evidência temporal elegível; não calcule a duração pelo relógio do navegador.',
  },
  'section-load-failed': {
    impact:
      'A atualização desta métrica falhou e pode não refletir a revisão mais recente.',
    action:
      'Tente atualizar novamente; use o último valor apenas como referência desatualizada.',
  },
};

const CAUSES: Record<NonNullable<MetricReason>, string> = {
  'no-usage-observed':
    'Nenhum evento de uso foi observado até o corte desta revisão.',
  'usage-id-missing':
    'Há evento de uso observado sem identidade estável para reconciliação.',
  'token-side-not-reported':
    'Este lado da contagem não foi reportado pela fonte observada.',
  'legacy-measurement-only':
    'Somente registros de medição antiga existem para esta atividade.',
  'cost-source-not-received':
    'Custo indisponível: fonte de custo não recebida.',
  'time-window-unavailable':
    'A janela ainda não possui início e fim elegíveis.',
  'section-load-failed': 'A seção de métricas não pôde ser atualizada.',
};

export function metricReasonGuidance(
  reason: MetricReason,
): Pick<CockpitMetricView, 'impact' | 'action'> {
  return reason ? GUIDANCE[reason] : { impact: null, action: null };
}

export function metricReasonCause(reason: MetricReason): string | null {
  return reason ? CAUSES[reason] ?? null : null;
}
